import { defineAsyncComponent } from 'vue'
import { storeToRefs } from 'pinia'
import { BASE_URL } from 'common/config.js'
import { useBonusSportModal } from 'src/hooks/useBonusSportModal'
import { useModalsStore } from 'src/stores/modals'
import { useSystemStore } from 'src/stores/system'
import { useUserAuthStore } from 'src/stores/user/auth'
import { useUserBonusesStore } from 'src/stores/user/bonuses'
import { $apollo } from 'src/apollo/apollo-options-api'
import { BONUS_STEP_CONDITIONS } from 'src/common/dictionary'
import { clearTrailingSlash, consoleLogAPI, getRedirectLocale } from 'src/common/functions'
import { defineApp, postMessageFromWebviewToApp } from 'src/common/webview'
import { getConfig } from 'src/configs'
import olimpBookmaker from 'src/router/olimp/bookmaker.js'
import olimpExtra from 'src/router/olimp/extra.js'
import olimpLandings from 'src/router/olimp/landings.js'
import olimpProfile from 'src/router/olimp/profile.js'
import olimpWebview from 'src/router/olimp/webview.js'
import olimpbetLandings from 'src/router/olimpbet/landings.js'
import olimpbetProfile from 'src/router/olimpbet/profile.js'
import olympBookmaker from 'src/router/olymp/bookmaker.js'
import olympExtra from 'src/router/olymp/extra.js'
import olympLandings from 'src/router/olymp/landings.js'
import olympProfile from 'src/router/olymp/profile.js'
import olympWebview from 'src/router/olymp/webview.js'
import getGameStatus from 'src/apollo/queries/getGameStatus.gql'

const locale = `:locale(${process.env.LOCALES})?`

const config = getConfig()

let extraRoutes = []
let landingsRoutes = []
let profileRoutes = []
let webviewRoutesFunction = function () {}
let bookmakerRoutesFunction = function () {}

if (process.env.PLATFORM === 'olymp') {
  profileRoutes = olympProfile
  extraRoutes = olympExtra
  landingsRoutes = olympLandings
  webviewRoutesFunction = olympWebview
  bookmakerRoutesFunction = olympBookmaker
} else if (process.env.PLATFORM === 'olimpbet') {
  profileRoutes = olimpbetProfile
  extraRoutes = olimpExtra
  landingsRoutes = olimpbetLandings
  webviewRoutesFunction = olimpWebview
  bookmakerRoutesFunction = olimpBookmaker
} else {
  profileRoutes = olimpProfile
  extraRoutes = olimpExtra
  landingsRoutes = olimpLandings
  webviewRoutesFunction = olimpWebview
  bookmakerRoutesFunction = olimpBookmaker
}

const OWN_SPORT = !!process.env.OWN_SPORT

function routes() {
  const userAuthStore = useUserAuthStore()
  const isBot = useSystemStore().isBot
  let bookmakerRoutes = []
  if (OWN_SPORT) {
    bookmakerRoutes = bookmakerRoutesFunction()
  }

  const casRoutesBase = OWN_SPORT ? 'casino' : ''

  const baseRoutes = [
    {
      path: 'favorites',
      name: 'Favorites',
      component: () => import('pages/Favorites.vue'),
      beforeEnter: (to, from, next) => {
        const isAuth = userAuthStore.getAuth
        if (isAuth) {
          next()
          return
        }

        next({ name: 'Home' })
      },
    },
    {
      path: 'latest',
      name: 'latestGames',
      component: () => import('pages/LatestGames.vue'),
    },
    {
      path: 'competition/:id?',
      name: 'Competition',
      meta: { auth: false },
      component: () => import('components/competitions/CompetitionNew.vue'),
    },
    {
      path: 'promotion/:id',
      name: 'Promotion',
      component: () => import('pages/PromotionItem.vue'),
    },
    {
      path: 'jackpot/:id',
      name: 'Jackpot',
      component: () => import('pages/Jackpot.vue'),
    },
    {
      path: 'jackpot/:id/win',
      name: 'JackpotWinners',
      component: () => import('pages/JackpotWinners.vue'),
    },
    {
      path: 'promo/:type',
      name: 'Promotions',
      meta: { auth: false },
      component: () => import('src/pages/Promotions.vue'),
    },
    {
      path: 'promo',
      name: 'PromotionsAll',
      redirect: 'promo/all',
    },
    {
      name: 'cashback',
      path: 'cashback',
      component: () => import('src/pages/Cashback.vue'),
    },
    {
      name: 'rules',
      path: 'rules',
      component: () => import('src/pages/Rules.vue'),
    },
    {
      name: 'top-wins',
      path: 'top-wins',
      component: () => import('src/pages/TopWins.vue'),
    },
    {
      name: 'cabbage-days',
      path: 'cabbage-days',
      component: () => import('src/pages/Cabbage-days.vue'),
    },
    {
      name: 'rule',
      path: 'rule/:rule',
      component: () => import('src/pages/Rule.vue'),
    },
    {
      name: 'ruleCrypto',
      path: 'rules/crypto',
      component: () => import('src/pages/RuleCrypto.vue'),
    },
    {
      name: 'ruleNew',
      path: 'rules/:slug',
      component: () => import('src/pages/RuleNew.vue'),
    },
    {
      path: 'auth-social/login/',
      name: 'auth-social',
      component: () => import('components/auth/AuthSocialWindow.vue'),
    },
    {
      path: 'result',
      name: 'Result',
      meta: { auth: false },
      component: () => import('pages/Result.vue'),
    },
    {
      path: 'ios-web-app',
      name: 'IosWebApp',
      meta: { auth: false },
      component: () => import('pages/IosWebApp.vue'),
    },
    {
      path: '',
      component: () => import('layouts/MainLayout.vue'),
      children: [
        {
          path: '',
          name: 'Home',
          meta: { auth: false },
          component: () => import('src/pages/Home.vue'),
        },
        {
          path: config.groupRootPath,
          name: 'Casino',
          redirect: { name: 'GamesByGroup', params: { group: 'slot' } },
        },
        {
          path: `${config.groupRootPath}/:group/:subgroup`,
          name: 'GamesBySubGroup',
          component: () => import('pages/GameGroup.vue'),
        },
        {
          path: `${config.groupRootPath}/:group`,
          name: 'GamesByGroup',
          component: () => import('pages/GameGroup.vue'),
        },
        {
          path: 'games/user-bonus',
          name: 'BonusGames',
          meta: { auth: true },
          component: () => import('pages/GamesByUserBonus.vue'),
        },
        {
          path: 'games/:manufacturer',
          name: 'GamesByManufacturer',
          component: () => import('pages/GamesByManufacturer.vue'),
        },
      ],
    },
    {
      name: 'GamePageNew',
      path: 'games/:manufacturer?/:game?',
      meta: { isFooterHidden: true, isLiveChatHidden: true, isHeaderSliderHidden: true },
      component: () => import('layouts/GameLayoutNew.vue'),
      beforeEnter: (to, from, next) => {
        const isAuth = useUserAuthStore().getAuth
        const variables = {
          manufacturer: to.params.manufacturer,
          supplier: to.query.supplier,
          free: !useUserAuthStore().getToken ? true : to.query.free === '1',
          s: to.query.s,
          freegame: to.query.freegame,
          slug: to.params.game,
          withoutInit: true,
        }

        if (!isAuth) {
          return next()
        }

        $apollo
          .query({
            query: getGameStatus,
            variables,
            fetchPolicy: 'network-only',
          })
          .then(({ data }) => {
            if (
              data.gameInit.statusCode !== 'DISABLED_BY_PROMO' ||
              useUserBonusesStore().getActiveStep?.condition === BONUS_STEP_CONDITIONS.TURNOVER
            ) {
              const { checkAndShowNonBonusVagerGameModal } = useBonusSportModal()
              if (
                from.name !== undefined &&
                checkAndShowNonBonusVagerGameModal(data.gameInit.game.gameId, () => next())
              ) {
                return
              }

              next()
              return
            }
            const openNonBonusGameModal = (gamePath) => {
              useModalsStore().pushModal({
                componentName: defineAsyncComponent(() => import('src/components/games/NonBonusGameModal.vue')),
                props: { gamePath },
              })
            }

            if (from.name !== undefined) {
              openNonBonusGameModal(() => next())
              return
            }

            next()

            if (useSystemStore().altW && useUserAuthStore().getUserBonusBalanceOn) {
              openNonBonusGameModal(() => {})
            }
          })
          .catch((e) => {
            consoleLogAPI(e)
            next()
          })
      },
    },
  ]

  const welcomeRoute = {
    name: 'Welcome',
    path: '/welcome',
    component: () => import('src/pages/Welcome.vue'),
    beforeEnter: (to, from, next) => {
      const isAuth = useUserAuthStore().getAuth

      if (isAuth) {
        next({ name: 'Home' })
        return
      }
      next()
    },
  }

  const errorRoute = {
    path: ':catchAll(.*)*',
    name: '404page',
    meta: { auth: false, isFooterHidden: true },
    component: () => import('src/pages/NotFound.vue'),
  }

  const childrenRoutes = OWN_SPORT
    ? [
        ...bookmakerRoutes,
        ...extraRoutes,
        ...profileRoutes,
        ...baseRoutes.map((item) => {
          if (item.path) {
            item.path = `${casRoutesBase}/${item.path}`
          } else {
            item.path = casRoutesBase
          }
          return item
        }),
        errorRoute,
      ]
    : [...extraRoutes, ...profileRoutes, ...baseRoutes, errorRoute]

  const competitionRoute = {
    path: OWN_SPORT ? `${casRoutesBase}/competitions/:type?` : 'competitions/:type?',
    name: 'Competitions',
    meta: { auth: false, isSeoBlockHidden: true },
    component: () => import('pages/CompetitionsList.vue'),
  }

  const { altW } = storeToRefs(useSystemStore())

  if (!altW.value) {
    childrenRoutes.push(competitionRoute)
  }

  const webviewRoutes = process.env.WEBVIEW ? webviewRoutesFunction(locale) : []

  const ignoredRoutes = ['euro_slots', 'new-year']
  const land = landingsRoutes.length
    ? [
        {
          path: `/${locale}/land`,
          component: () => import('layouts/LandingLayout.vue'),
          children: landingsRoutes,
          beforeEnter: (to, from, next) => {
            const isUserAccount = localStorage.getItem('hasAccount') || useUserAuthStore().getUser
            isUserAccount && !ignoredRoutes.includes(to.name)
              ? next({
                  name: 'Home',
                  query: to.query,
                })
              : next()
          },
        },
      ]
    : []
  const additionalRoutes =
    useSystemStore().alt && !isBot
      ? [
          {
            path: '/session-auth',
            name: 'session-auth',
            meta: { auth: false, isFooterHidden: true, isSeoBlockHidden: true },
            component: () => import('src/pages/AuthBySession.vue'),
            beforeEnter(to, from, next) {
              const session = to.query.session
              if (!session) {
                const url = to.query.url ? to.query.url.replace(BASE_URL, '') : null
                next(url || { name: 'Home' })
              }
              next()
            },
          },
        ]
      : []

  const loyalty = [
    {
      path: '/loyalty',
      children: [
        {
          path: '',
          component: () => import('layouts/CommonLayout.vue'),
          children: [
            {
              path: '',
              name: 'LoyaltyHome',
              component: () => import('pages/loyalty/landing/LoyaltyLanding.vue'),
            },
          ],
        },
        {
          path: '',
          component: () => import('layouts/LoyaltyLayout.vue'),
          children: [
            {
              path: '',
              component: () => import('layouts/CommonLayout.vue'),
              children: [
                {
                  name: 'LoyaltyAchievements',
                  path: 'achievements',
                  meta: { auth: true },
                  component: () => import('pages/loyalty/LoyaltyAchievements.vue'),
                },
                {
                  name: 'LoyaltyShop',
                  path: 'shop',
                  meta: { auth: true },
                  component: () => import('pages/loyalty/LoyaltyShop.vue'),
                },
              ],
            },
            {
              path: 'map',
              component: () => import('layouts/LandingLayout.vue'),
              children: [
                {
                  path: '',
                  name: 'LoyaltyMap',
                  meta: { auth: true },
                  component: () => import('src/pages/loyalty/Map.vue'),
                },
              ],
            },
          ],
        },
      ],
    },
  ]

  return [
    ...land,
    ...(process.env.PLATFORM === 'olymp' ? [...loyalty, welcomeRoute] : []),
    ...webviewRoutes,
    {
      path: `/${locale}`,
      component: () => import('layouts/CommonLayout.vue'),
      children: childrenRoutes,
      beforeEnter(to, from, next) {
        if (defineApp() === 'mav' && to.hash.startsWith('#payment_')) {
          return postMessageFromWebviewToApp(to.hash.replace('#', ''))
        }

        next()
      },
    },
    ...additionalRoutes,
    {
      path: '/*',
      redirect: (to) => {
        const url = `${getRedirectLocale()}${to.path}`.replace('//', '/')
        return clearTrailingSlash(url)
      },
    },
  ]
}
export default routes
